import * as React from 'react'

import {
  Alert,
  Avatar,
  Box,
  Button,
  ProgressBar,
  Text,
  useConfirmationDialog,
} from '@factorialco/deprecated-design-system'
import {
  ExpensesSubscriptionMembersDatatableDocument,
  ProjectionKind,
  useExpensesSubscriptionMembersDatatable,
} from 'generated/resources/datatable.graphql'
import {
  ExpensesSubscriptionsFeatureMembershipDocument,
  ExpensesSubscriptionsPartnerManagedDocument,
  ExpensesSubscriptionsSearchByEmployeeDocument,
  RemoveExpensesPlanEmployeesDocument,
} from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import Loading from 'modules/core/components/Loading'
import Popover from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import InfoTooltip from 'modules/expenses/components/subscription/InfoTooltip'

interface Props {
  onAddPressed: () => void
  managedByPartnersUserLimit: number
}

const ExpensesSubscriptionUsersDatatable = ({
  onAddPressed,
  managedByPartnersUserLimit,
}: Props) => {
  const { company } = useCurrentSession()
  const { openDialog } = useConfirmationDialog()
  const { search, onSearch, debouncedSearch } = useDebouncedSearch('', { minimumCharacters: 3 })

  const [removeMembership, { loading }] = useMutation(RemoveExpensesPlanEmployeesDocument, {
    refetchQueries: [
      ExpensesSubscriptionMembersDatatableDocument,
      ExpensesSubscriptionsFeatureMembershipDocument,
    ],
    extractorFn: (data) => data.featuresMutations.updateMembersFeaturesFeatureMembership,
  })

  const { data: featureMembership, loading: loadingFeatureMembership } = useQuery(
    ExpensesSubscriptionsFeatureMembershipDocument
  )

  const { data: employeeSearchData } = useQuery(ExpensesSubscriptionsSearchByEmployeeDocument, {
    variables: { employeeName: debouncedSearch },
    skip: !debouncedSearch,
  })

  const searchAccessIds = employeeSearchData?.employees.employeeNames.map((e) => e.access.id)

  const { data: partnerManagedData } = useQuery(ExpensesSubscriptionsPartnerManagedDocument, {
    variables: { companyId: company.id },
  })
  const partnerManaged = partnerManagedData?.features.featuresConnection?.nodes?.some(
    (node) => node?.enabled
  )

  const source = useExpensesSubscriptionMembersDatatable(
    [
      {
        identifier: 'employee',
        colSpan: 9,
        title: i18n.t('billing.subscription.plans.expenses.columns.user'),
        kind: ProjectionKind.Custom,
        template: '{{ access.fullName }}',
        render: ({ access }) => {
          return (
            <Box flexDirection="row" alignItems="center" gap="s16" minWidth="s96">
              <Avatar size="s40" url={buildUrl(access?.avatar)} label={access?.fullName || ''} />
              <Text.Normal size="200" weight="medium">
                {access?.fullName || ''}
              </Text.Normal>
            </Box>
          )
        },
      },
      {
        identifier: 'addedOn',
        colSpan: 2,
        title: i18n.t('billing.subscription.plans.expenses.columns.added_on'),
        kind: ProjectionKind.Custom,
        template: '{{ createdAt }}',
        render: ({ createdAt }) => {
          return (
            <Text.Normal size="200" weight="medium">
              {createdAt ? dateToString(stringToDate(createdAt, 'backend_datetime'), 'ui') : ''}
            </Text.Normal>
          )
        },
      },
      {
        identifier: 'options',
        colSpan: 1,
        kind: ProjectionKind.Decorative,
        render: (membership) => (
          <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
            <Popover type="list">
              <Popover.Trigger>
                <Button.Decorative type="secondary" iconName="ellipsis" />
              </Popover.Trigger>
              <Popover.Content side="bottom" align="end">
                <Popover.TouchableOption
                  onClick={() =>
                    openDialog({
                      title: i18n.t('billing.subscription.plans.expenses.remove_user_modal.title', {
                        employee_name: membership.access.fullName,
                      }),
                      onConfirm: () => {
                        if (membership?.access?.employee) {
                          removeMembership({
                            variables: {
                              employeeIds: [membership.access.employee.id],
                              companyId: company.id,
                            },
                          })
                        }
                      },
                      content: i18n.t(
                        'billing.subscription.plans.expenses.remove_user_modal.description',
                        {
                          employee_name: membership.access.fullName,
                        }
                      ),
                    })
                  }
                  title={i18n.t('billing.subscription.plans.expenses.actions.remove_user')}
                  danger
                  disabled={loading}
                />
              </Popover.Content>
            </Popover>
          </Box>
        ),
      },
    ],
    {
      variables: {
        companyId: company.id,
        accessIds: searchAccessIds,
      },
    }
  )
  if (loadingFeatureMembership) {
    return <Loading />
  }

  const currentUserCount =
    featureMembership?.features.featureMembership?.membersConnection.totalCount ?? 0

  const ManagedByPartnersUserLimitBanner = ({ limit }: { limit: number }) => {
    if (currentUserCount / limit >= 1) {
      return (
        <Alert color="error">
          <Text size="200" weight="medium">
            {i18n.t('billing.subscription.plans.expenses.managed_by_partners.usage_alert_banner')}
          </Text>
        </Alert>
      )
    } else if (currentUserCount / limit >= 0.8) {
      return (
        <Alert color="warning">
          <Text size="200" weight="medium">
            {i18n.t(
              'billing.subscription.plans.expenses.managed_by_partners.usage_threshold_banner'
            )}
          </Text>
        </Alert>
      )
    }
  }

  return (
    <Box>
      {featureMembership?.features.featureMembership && (
        <DataTable.Provider
          persistFilters={false}
          source={source}
          pagination={{
            kind: 'cursor',
            itemsPerPage: 25,
          }}
        >
          <Box gap="s12">
            <DataTable.HeadBar
              toolbar={
                partnerManaged && managedByPartnersUserLimit > 0 ? (
                  <Box flexDirection="row" alignItems="center" gap="s8">
                    <Box width="s200">
                      <ProgressBar
                        amount={currentUserCount}
                        total={managedByPartnersUserLimit}
                        showLabel
                      />
                    </Box>
                    <InfoTooltip
                      label={i18n.t(
                        'billing.subscription.plans.expenses.managed_by_partners.tooltip',
                        {
                          current: currentUserCount,
                          limit: managedByPartnersUserLimit,
                        }
                      )}
                    />
                  </Box>
                ) : undefined
              }
              actions={
                <Button iconName="add" onClick={onAddPressed}>
                  {i18n.t('billing.subscription.plans.expenses.actions.add_users')}
                </Button>
              }
              exportFileName={i18n.t('billing.subscription.plans.expenses.export_file_name')}
              search={{ onChange: onSearch, value: search, placeholder: i18n.t('common.search') }}
            />
            {partnerManaged && managedByPartnersUserLimit > 0 && (
              <ManagedByPartnersUserLimitBanner limit={managedByPartnersUserLimit} />
            )}
            <DataTable.Table />
          </Box>
        </DataTable.Provider>
      )}
    </Box>
  )
}

export default ExpensesSubscriptionUsersDatatable
